import { useController, FieldValues, Path, PathValue, useFormContext } from "react-hook-form";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import Box from "ds/components/Box";
import Typography from "ds/components/Typography";
import useDefaultRoleAssignmentMethods from "views/Account/Settings/AssignRoleForm/useDefaultRoleAssignmentMethods";
import AssignRoleForm from "views/Account/Settings/AssignRoleForm";

type FormFieldRoleAssignmentProps<T extends FieldValues = FieldValues> = {
  name: Path<T>;
  availableRoles: Array<{ id: string; name: string }>;
  loading?: boolean;
  required?: boolean;
  setHasDirtyFields: (hasUnfinishedChanges: boolean) => void;
};

const FormFieldRoleAssignment = <T extends FieldValues = FieldValues>({
  name,
  availableRoles,
  loading = false,
  required = false,
  setHasDirtyFields,
}: FormFieldRoleAssignmentProps<T>) => {
  const [dirtyFields, setDirtyFields] = useState<Record<string, boolean>>({});

  const hasDirtyFields = useMemo(() => Object.values(dirtyFields).some(Boolean), [dirtyFields]);

  const { control } = useFormContext();

  const { field, fieldState } = useController({
    name,
    control,
    defaultValue: [] as PathValue<T, Path<T>>,
    rules: required
      ? {
          validate: (value) => {
            if (!value || value.length === 0) {
              return "At least one role assignment is required";
            }
            return true;
          },
        }
      : undefined,
  });

  const { onChange } = field;

  const { onDelete, onCreate, onEdit, assignedRoles } = useDefaultRoleAssignmentMethods();

  const prevAssignedRoles = useRef(assignedRoles);

  useEffect(() => {
    setHasDirtyFields(hasDirtyFields);
  }, [hasDirtyFields, setHasDirtyFields]);

  // Sync assignedRoles to form when they actually change
  useEffect(() => {
    if (prevAssignedRoles.current !== assignedRoles) {
      onChange(assignedRoles);
      prevAssignedRoles.current = assignedRoles;
    }
  }, [assignedRoles, onChange]);

  const handleDirtyFields = useCallback((formId: string, value: boolean) => {
    setDirtyFields((prev) => ({ ...prev, [formId]: value }));
  }, []);

  return (
    <Box direction="column" gap="large" fullWidth>
      <AssignRoleForm
        onCreate={onCreate}
        onEdit={onEdit}
        onDelete={onDelete}
        setDirtyFields={handleDirtyFields}
        availableRoles={availableRoles}
        assignedRoles={assignedRoles}
        loading={loading}
      />
      {fieldState.error && (
        <Typography variant="p-body3" color="danger" tag="span">
          {fieldState.error.message}
        </Typography>
      )}
    </Box>
  );
};

export default FormFieldRoleAssignment;
